import {
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
  type FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query/react';

interface Properties {
  readonly baseUrl: string;
  readonly getAccessToken: () => Promise<string>;
  readonly prepareHeaders?: (headers: Headers) => void | Promise<void>;
}

const authenticationRequiredError: FetchBaseQueryError = {
  status: 401,
  data: 'Authentication required',
};

// The empty extra-options type keeps endpoint options optional, matching fetchBaseQuery.
// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- RTK uses an empty extra-options object for optional endpoint options.
type AuthenticatedBaseQuery = BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, {}, FetchBaseQueryMeta>;

const createAuthenticatedBaseQuery = ({
  baseUrl,
  getAccessToken,
  prepareHeaders,
}: Properties): AuthenticatedBaseQuery => {
  const baseQuery = fetchBaseQuery({
    baseUrl,
    prepareHeaders: async (headers) => {
      await prepareHeaders?.(headers);
      return headers;
    },
  });

  return async (requestArguments, api, extraOptions) => {
    const accessToken = await getAccessToken();

    if (!accessToken) {
      return { error: authenticationRequiredError };
    }

    const request = typeof requestArguments === 'string' ? { url: requestArguments } : { ...requestArguments };
    const headers = new Headers(request.headers as unknown as HeadersInit);
    headers.set('authorization', `Bearer ${accessToken}`);

    return baseQuery({ ...request, headers }, api, extraOptions);
  };
};

export default createAuthenticatedBaseQuery;
